import { css } from '@emotion/react';
import type { UseOptionReturnValue } from '@mui/base';
import {
	ClickAwayListener,
	SelectProvider,
	useOption,
	useSelect,
} from '@mui/base';
import useForkRef from '@mui/material/utils/useForkRef';
import type { MutableRefObject, ReactNode, Ref } from 'react';
import { useMemo, useState } from 'react';

import IconX from '~icons/bx/x';
import IconChevronDown from '~icons/ci/chevron-down';

import type { UnpluginIcon } from '@adflow/frontend/ui-assets';
import {
	AnimatedPopover,
	lineClampStyle,
	notchedPopperModifier,
	sameWidthPopperModifier,
} from '@adflow/frontend/ui-common';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { theme, typography } from '@adflow/frontend/ui-theme';
import { useDebounceSearch } from '@adflow/frontend/util-common';

import { selectorStyles } from './styles';
import type { Selector } from './types';

const styles = {
	helperText: css(typography.inputHelper, {
		fontSize: '12px',
	}),
	helperTextError: css({
		color: theme.colors.palettes.error.dark,
	}),
	loading: css({
		position: 'relative',
		width: '2rem',
		height: '2rem',
		right: '-0.25rem',
	}),
};

function uniqPush<T>(array: readonly T[], item: T) {
	return array.includes(item) ? array : [...array, item];
}

export type SelectorSelectRenderOption = (
	value: string,
	getRootProps: UseOptionReturnValue['getRootProps'],
	highlighted: UseOptionReturnValue['highlighted']
) => ReactNode;

export interface SelectorSelectProps extends Omit<Selector, 'inputRef'> {
	Icon?: UnpluginIcon;
	defaultValue?: string;
	value: string;
	onChange: (selected: string) => void;
	popoverRef?: MutableRefObject<HTMLDivElement | null>;
	helperText?: ReactNode;
	removeButton?: boolean;
	isLoading?: boolean;
	renderOption?: SelectorSelectRenderOption;

	inputRef?: Ref<HTMLButtonElement>;
}

export function SelectorSelect({
	dropdownMaxHeight = '40vh',
	disabled,
	archived,
	error,
	readOnly,
	placeholder,
	items,
	value,
	onChange,
	popoverRef,
	testid,
	helperText,
	inputRef,
	removeButton,
	Icon: IconProp = IconChevronDown,
	isLoading,
	renderOption,
}: SelectorSelectProps) {
	const [buttonElement, setButtonElement] = useState<Element | null>(null);

	const Icon = useMemo(
		() => (value && removeButton ? IconX : IconProp),
		[IconProp, removeButton, value]
	);

	const { getButtonProps, open, getListboxProps, contextValue, dispatch } =
		useSelect({
			buttonRef: setButtonElement,
			// `useSelect` expects `value` to be in `options`, so push it if it's not.
			options: uniqPush(items, value).map((item) => ({
				label: item,
				value: item,
			})),
			value,
			onChange: (_event, value_) => {
				if (value_ === null) {
					// We don't support clearing selected values so this should never happen.
					console.warn('SelectorSelect: value is null');
					return;
				}
				onChange(value_);
			},
			disabled,
		});

	const { ref: buttonPropsRef, ...restButtonProps } = getButtonProps();
	const mergedButtonRef = useForkRef(buttonPropsRef, inputRef);

	const debounceOpen = useDebounceSearch(open, 50);

	const valueExistsInItems = useMemo(
		() => value && items.includes(value),
		[value, items]
	);

	// If value doesn't exist in items, show it with reduced opacity to indicate it's invalid
	const shouldShowReducedOpacity = useMemo(
		() => value && !valueExistsInItems,
		[value, valueExistsInItems]
	);

	return (
		<>
			{readOnly ? (
				<div
					css={selectorStyles.container({
						error,
						disabled,
						archived,
						readOnly,
					})}
				>
					<div
						css={[
							selectorStyles.input({ disabled, archived, readOnly }),
							{ cursor: 'default', resize: 'none', wordBreak: 'break-word' },
							shouldShowReducedOpacity ? css({ opacity: 0.5 }) : undefined,
						]}
					>
						{value || <span>&nbsp;</span>}
					</div>
				</div>
			) : null}
			<button
				{...restButtonProps}
				ref={mergedButtonRef}
				type="button"
				css={[
					selectorStyles.container({
						error,
						disabled,
						archived,
						readOnly,
					}),
					{
						textAlign: 'left',
						cursor: disabled ? 'default' : 'pointer',
						display: readOnly ? 'none' : undefined,
					},
				]}
				data-testid={`${testid}-button`}
			>
				<div
					css={[
						lineClampStyle(),
						selectorStyles.input({ disabled, archived, readOnly }),
						{ resize: 'none' },
						value
							? shouldShowReducedOpacity
								? css({ opacity: 0.5 })
								: undefined
							: css(typography.placeholder, {
									opacity: 1,
								}),
					]}
					data-testid={`${testid}-input`}
				>
					{value || placeholder}
				</div>
				{isLoading ? (
					<div css={styles.loading}>
						<LoadingIndicator />
					</div>
				) : null}
				{readOnly ? null : (
					<div>
						<Icon
							onClick={value && removeButton ? () => onChange('') : undefined}
							css={selectorStyles.adornmentIcon}
							width={20}
							height={20}
						/>
					</div>
				)}
			</button>

			{disabled || readOnly ? null : (
				<ClickAwayListener
					onClickAway={(event) =>
						debounceOpen
							? dispatch({ type: 'list:blur', event: event as never })
							: undefined
					}
				>
					<AnimatedPopover
						ref={popoverRef}
						open={open}
						anchorEl={buttonElement}
						PopperProps={{
							placement: 'bottom-start',
							keepMounted: true,
							modifiers: [
								{ name: 'offset', options: { offset: [0, -2] } },
								notchedPopperModifier({ notchDepth: 1 }),
								sameWidthPopperModifier,
							],
						}}
						data-testid={`${testid}-popover`}
					>
						<SelectProvider value={contextValue}>
							<ul
								{...getListboxProps()}
								css={[
									selectorStyles.popover(dropdownMaxHeight),
									selectorStyles.list,
								]}
							>
								{items.map((item, index) => (
									<Option
										key={`${item}${index}`}
										value={item}
										renderOption={renderOption}
									/>
								))}
							</ul>
						</SelectProvider>
					</AnimatedPopover>
				</ClickAwayListener>
			)}

			{helperText ? (
				<div css={[styles.helperText, error && styles.helperTextError]}>
					{helperText}
				</div>
			) : null}
		</>
	);
}

interface OptionProps {
	value: string;
	disabled?: boolean;
	renderOption?: SelectorSelectRenderOption;
}

function Option({ value, disabled = false, renderOption }: OptionProps) {
	const { getRootProps, highlighted } = useOption({
		value,
		disabled,
		label: value,
	});

	const rendered = renderOption?.(value, getRootProps, highlighted);

	if (rendered) {
		return rendered;
	}

	return (
		<li
			{...getRootProps()}
			data-highlighted={highlighted || undefined}
			// `data-value` is only used by tests.
			data-value={value}
			css={selectorStyles.listItem}
		>
			{value}
		</li>
	);
}
